import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, Navigation } from 'lucide-react';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';
import { t, getCurrentLanguage } from '../utils/translations';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    // Écouter l'événement de changement de langue
    window.addEventListener('languageChanged', handleLanguageChange);

    // Écouter aussi les changements i18n
    const i18nListener = () => {
      const newLang = getCurrentLanguage();
      setCurrentLanguage(newLang);
    };

    if (window.i18n) {
      window.i18n.on('languageChanged', i18nListener);
    }

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      if (window.i18n) {
        window.i18n.off('languageChanged', i18nListener);
      }
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Ici vous pouvez ajouter la logique pour envoyer le formulaire
    // Par exemple, un appel API ou une redirection
    alert(currentLanguage === 'en' ? 'Form submitted successfully!' : 'Formulaire soumis avec succès !');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex-grow">
        {/* Enhanced Hero Section */}
        <section className="relative h-[400px] mb-16 overflow-hidden">
          {/* Enhanced Molecular Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#089556] via-[#0b6f47] to-[#07D1EB]">
            <div className="absolute inset-0 opacity-[0.3]">
              <div className="absolute inset-0" style={{
                backgroundImage: `
                  radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 3px, transparent 3px),
                  radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 2.5px, transparent 2.5px),
                  radial-gradient(circle at 40% 80%, rgba(255,255,255,0.7) 2px, transparent 2px),
                  radial-gradient(circle at 70% 20%, rgba(255,255,255,0.5) 3.5px, transparent 3.5px),
                  radial-gradient(circle at 90% 50%, rgba(255,255,255,0.8) 2.8px, transparent 2.8px),
                  radial-gradient(circle at 30% 60%, rgba(255,255,255,0.6) 2.2px, transparent 2.2px)
                `,
                backgroundSize: '200px 200px, 160px 160px, 220px 220px, 180px 180px, 190px 190px, 150px 150px'
              }}></div>
              
              {/* Enhanced Connection Lines */}
              <div className="absolute inset-0" style={{
                backgroundImage: `
                  linear-gradient(35deg, transparent 48%, rgba(255,255,255,0.4) 49%, rgba(255,255,255,0.4) 51%, transparent 52%),
                  linear-gradient(-35deg, transparent 48%, rgba(255,255,255,0.3) 49%, rgba(255,255,255,0.3) 51%, transparent 52%),
                  linear-gradient(70deg, transparent 48%, rgba(255,255,255,0.35) 49%, rgba(255,255,255,0.35) 51%, transparent 52%)
                `,
                backgroundSize: '350px 350px, 320px 320px, 380px 380px',
                opacity: '0.5'
              }}></div>
            </div>

            {/* Enhanced Floating Molecules */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-1/6 left-1/4 w-5 h-5 bg-white rounded-full opacity-20 animate-pulse shadow-lg shadow-white/40"></div>
              <div className="absolute top-2/3 right-1/4 w-4 h-4 bg-white rounded-full opacity-30 animate-bounce shadow-lg shadow-white/50"></div>
              <div className="absolute top-1/2 left-3/4 w-4.5 h-4.5 bg-white rounded-full opacity-25 animate-pulse shadow-lg shadow-white/45"></div>
              <div className="absolute top-1/4 right-1/3 w-3 h-3 bg-white rounded-full opacity-35 animate-bounce shadow-lg shadow-white/40"></div>
              <div className="absolute bottom-1/6 left-1/6 w-3.5 h-3.5 bg-white rounded-full opacity-25 animate-pulse shadow-lg shadow-white/50"></div>
            </div>
          </div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-8 shadow-xl border border-white/30">
                <MessageCircle className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight drop-shadow-lg mb-4">
                {t('contact.title')}
              </h1>
              <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-sm">
                {t('contact.subtitle')}
              </p>
              <div className="w-32 h-1 bg-white/60 mx-auto mt-6 rounded-full"></div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Enhanced Contact Information */}
            <div className="relative overflow-hidden">
              {/* Molecular Background */}
              <div className="absolute inset-0 opacity-[0.06]">
                <div className="absolute inset-0" style={{
                  backgroundImage: `
                    radial-gradient(circle at 30% 30%, #089556 2px, transparent 2px),
                    radial-gradient(circle at 70% 70%, #07D1EB 1.5px, transparent 1.5px)
                  `,
                  backgroundSize: '160px 160px, 130px 130px'
                }}></div>
              </div>

              <div className="relative z-10 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 border border-white/20 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center gap-4 mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                    <Navigation className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-[#089556]">{t('contact.coordinates.title')}</h2>
                    <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
                  </div>
                </div>
                
                <div className="space-y-8">
                  <div className="group flex items-start space-x-4 p-4 bg-gradient-to-r from-[#089556]/5 to-[#07D1EB]/5 rounded-xl border border-white/20 hover:bg-gradient-to-r hover:from-[#089556]/10 hover:to-[#07D1EB]/10 transition-all duration-300">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2 text-[#089556] text-lg">{t('contact.coordinates.address.title')}</h3>
                      <p className="text-gray-600 text-justify leading-relaxed">
                        {t('contact.coordinates.address.street')}<br />
                        {t('contact.coordinates.address.city')}
                      </p>
                    </div>
                  </div>

                  <div className="group flex items-start space-x-4 p-4 bg-gradient-to-r from-[#089556]/5 to-[#07D1EB]/5 rounded-xl border border-white/20 hover:bg-gradient-to-r hover:from-[#089556]/10 hover:to-[#07D1EB]/10 transition-all duration-300">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2 text-[#089556] text-lg">{t('contact.coordinates.phone.title')}</h3>
                      <p className="text-gray-600 text-lg font-medium">{t('contact.coordinates.phone.number')}</p>
                    </div>
                  </div>

                  <div className="group flex items-start space-x-4 p-4 bg-gradient-to-r from-[#089556]/5 to-[#07D1EB]/5 rounded-xl border border-white/20 hover:bg-gradient-to-r hover:from-[#089556]/10 hover:to-[#07D1EB]/10 transition-all duration-300">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2 text-[#089556] text-lg">{t('contact.coordinates.email.title')}</h3>
                      <p className="text-gray-600 text-lg font-medium">{t('contact.coordinates.email.address')}</p>
                    </div>
                  </div>

                  <div className="group flex items-start space-x-4 p-4 bg-gradient-to-r from-[#089556]/5 to-[#07D1EB]/5 rounded-xl border border-white/20 hover:bg-gradient-to-r hover:from-[#089556]/10 hover:to-[#07D1EB]/10 transition-all duration-300">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-2 text-[#089556] text-lg">{t('contact.coordinates.hours.title')}</h3>
                      <p className="text-gray-600 leading-relaxed">
                        <strong>{t('contact.coordinates.hours.weekdays')}:</strong> {t('contact.coordinates.hours.weekdaysTime')}<br />
                        <strong>{t('contact.coordinates.hours.saturday')}:</strong> {t('contact.coordinates.hours.saturdayTime')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Enhanced Map */}
                <div className="mt-8">
                  <h3 className="text-xl font-bold text-[#089556] mb-4">{t('contact.coordinates.map.title')}</h3>
                  <div className="rounded-2xl overflow-hidden shadow-lg border border-white/20">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63370.86!2d2.6167!3d6.4969!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1023552e0e4b3b3b%3A0x4564304de2c6e3a8!2sPorto-Novo%2C%20Benin!5e0!3m2!1sfr!2s!4v1624431278647!5m2!1sfr!2s"
                      className="w-full h-64"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      title="Carte Google Maps - Localisation AHB à Porto-Novo"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Contact Form */}
            <div className="relative overflow-hidden">
              {/* Molecular Background */}
              <div className="absolute inset-0 opacity-[0.06]">
                <div className="absolute inset-0" style={{
                  backgroundImage: `
                    radial-gradient(circle at 40% 60%, #089556 2px, transparent 2px),
                    radial-gradient(circle at 60% 40%, #07D1EB 1.5px, transparent 1.5px)
                  `,
                  backgroundSize: '170px 170px, 140px 140px'
                }}></div>
              </div>

              <div className="relative z-10 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 border border-white/20 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center gap-4 mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full shadow-lg">
                    <Send className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-[#089556]">{t('contact.form.title')}</h2>
                    <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] rounded-full mt-2"></div>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-[#089556] mb-2">
                      {t('contact.form.name.label')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#07D1EB] focus:border-[#07D1EB] outline-none transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm"
                      placeholder={t('contact.form.name.placeholder')}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#089556] mb-2">
                      {t('contact.form.email.label')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#07D1EB] focus:border-[#07D1EB] outline-none transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm"
                      placeholder={t('contact.form.email.placeholder')}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-[#089556] mb-2">
                      {t('contact.form.subject.label')}
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#07D1EB] focus:border-[#07D1EB] outline-none transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm"
                      placeholder={t('contact.form.subject.placeholder')}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-[#089556] mb-2">
                      {t('contact.form.message.label')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="6"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#07D1EB] focus:border-[#07D1EB] outline-none transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm resize-none"
                      placeholder={t('contact.form.message.placeholder')}
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white py-4 px-6 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3 font-semibold text-lg"
                  >
                    <Send className="w-5 h-5" />
                    <span>{t('contact.form.submit')}</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
